/**
 * Competitive rating: Elo, the tiers it is shown as, and when a match counts.
 *
 * SHARED between the client and the server on purpose. The server is the only
 * thing that ever WRITES a rating — see server/ratings.ts — but the client has
 * to render it, name its tier, and say how far the next one is, and a second
 * copy of the band table is a second thing to forget to update. Nothing here
 * touches a socket, a database or a clock.
 *
 * ===========================================================================
 * WHY ELO AND NOT SOMETHING CLEVERER
 * ===========================================================================
 *
 * Glicko and TrueSkill both model UNCERTAINTY as well as skill, which is
 * genuinely better — a player's rating should move further when the system
 * does not know much about them. Elo approximates that with one lever, `K`,
 * and this file uses it: `PROVISIONAL_MATCHES` games of a large K, then a
 * small one. That is most of the benefit for a tenth of the surface, and the
 * surface is the point — every number below is one a player can be told.
 *
 * The thing Elo genuinely cannot do is rate a team match per-player, and
 * neither can anything else without more information than a scoreline carries.
 * `teamRatingChange` takes the pragmatic route the whole genre takes: each
 * player is rated against the MEAN of the opposing side, and everyone on a
 * team moves together. A player who carried and one who idled get the same
 * points, which is wrong and is also what every ladder in this genre does,
 * because the alternative is scoring individual contribution and that is a
 * far larger lie.
 *
 * ===========================================================================
 * WHEN A MATCH COUNTS
 * ===========================================================================
 *
 * `isRanked` is the whole rule and it is deliberately strict: BOTH sides need
 * a human on them. A queue that has waited long enough fills the field with
 * bots (see `ModeRules` in src/net/modes.ts), and a rating won against five
 * bots is not a rating — it is a measure of how quiet the server was. Those
 * matches still go into the history, marked unranked, because "what did I play
 * last night" and "what am I rated" are different questions.
 */

// ---------------------------------------------------------------------------
// The numbers
// ---------------------------------------------------------------------------

/**
 * Where everyone starts.
 *
 * 600 rather than the traditional 1500 so the number reads as a score rather
 * than as a chess rating, and so the bottom tier is reachable downwards — a
 * new player who loses their first ten games should be able to sit at the
 * bottom of the ladder rather than clipping at zero.
 */
export const STARTING_RATING = 600;

/** No rating goes below this. A floor, so a bad week is not unrecoverable. */
export const MIN_RATING = 100;

/**
 * Matches before a rating is trusted. Under it the K factor is large, so ten
 * games is enough to land in roughly the right tier instead of thirty.
 */
export const PROVISIONAL_MATCHES = 10;

/** Rating points a completely unexpected result is worth, while provisional. */
export const K_PROVISIONAL = 64;
/** And afterwards. Two evenly matched players trade about 12 a game. */
export const K_ESTABLISHED = 24;

/** The spread that makes a 10:1 favourite. Standard Elo. */
const RATING_SCALE = 400;

export type MatchOutcome = 'win' | 'loss' | 'draw';

/** Elo's score for an outcome: the thing `expectedScore` is compared against. */
export const outcomeScore = (outcome: MatchOutcome): number =>
  outcome === 'win' ? 1 : outcome === 'draw' ? 0.5 : 0;

/** The outcome of a scoreline, from the given team's side. */
export const outcomeFor = (score: readonly [number, number], team: 0 | 1): MatchOutcome => {
  const mine = score[team];
  const theirs = score[team === 0 ? 1 : 0];
  if (mine > theirs) return 'win';
  if (mine < theirs) return 'loss';
  return 'draw';
};

/** Probability `rating` beats `opponent`, in Elo's logistic. */
export const expectedScore = (rating: number, opponent: number): number =>
  1 / (1 + Math.pow(10, (opponent - rating) / RATING_SCALE));

export const kFactor = (matchesPlayed: number): number =>
  matchesPlayed < PROVISIONAL_MATCHES ? K_PROVISIONAL : K_ESTABLISHED;

/**
 * The rating change for one player of one match.
 *
 * Rounded away from zero rather than to nearest, so a heavy favourite who wins
 * still gains a point instead of gaining nothing at all. A ladder where
 * winning can be worth exactly zero reads as broken even when the arithmetic
 * is right — and a rounding rule that could produce a stationary rating from a
 * decisive result would make climbing off the top of the ladder impossible.
 */
export const ratingChange = (
  rating: number,
  opponentRating: number,
  outcome: MatchOutcome,
  matchesPlayed: number
): number => {
  const raw =
    kFactor(matchesPlayed) * (outcomeScore(outcome) - expectedScore(rating, opponentRating));
  if (outcome === 'draw') return Math.round(raw);
  const rounded = raw >= 0 ? Math.ceil(raw) : Math.floor(raw);
  // A win is never worth less than a point, and a loss never costs less.
  if (outcome === 'win') return Math.max(1, rounded);
  return Math.min(-1, rounded);
};

/** Apply a change, respecting the floor. */
export const applyRatingChange = (rating: number, change: number): number =>
  Math.max(MIN_RATING, rating + change);

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

/** One rated participant, as the rating pass needs them. */
export interface RatedPlayer {
  readonly rating: number;
  readonly matchesPlayed: number;
  readonly team: 0 | 1;
}

export interface RatingResult {
  readonly before: number;
  readonly after: number;
  readonly change: number;
}

export const meanRating = (ratings: readonly number[]): number =>
  ratings.length === 0
    ? STARTING_RATING
    : ratings.reduce((sum, r) => sum + r, 0) / ratings.length;

/**
 * Whether a finished match moves anybody's rating.
 *
 * Takes the humans' teams, not the roster: bots are not in the list at all.
 * See the header for why both sides must have one.
 */
export const isRanked = (humanTeams: readonly (0 | 1)[]): boolean =>
  humanTeams.includes(0) && humanTeams.includes(1);

/**
 * Rate every human in a finished match, in one pass.
 *
 * Returns a result per input player, in the same order. The opponent mean is
 * computed from the ratings each player STARTED with — a sequential update, in
 * which the second player on a team is rated against opponents whose ratings
 * have already moved, makes the outcome depend on iteration order.
 *
 * An unranked match returns no change for anybody rather than an empty list:
 * the caller still has a history row to write, and it should not have to
 * remember which shape means what.
 */
export const rateMatch = (
  players: readonly RatedPlayer[],
  score: readonly [number, number]
): RatingResult[] => {
  const unchanged = players.map(p => ({ before: p.rating, after: p.rating, change: 0 }));
  if (!isRanked(players.map(p => p.team))) return unchanged;

  const means: [number, number] = [
    meanRating(players.filter(p => p.team === 0).map(p => p.rating)),
    meanRating(players.filter(p => p.team === 1).map(p => p.rating)),
  ];

  return players.map(player => {
    const outcome = outcomeFor(score, player.team);
    const opponent = means[player.team === 0 ? 1 : 0];
    const change = ratingChange(player.rating, opponent, outcome, player.matchesPlayed);
    const after = applyRatingChange(player.rating, change);
    // The floor can swallow part of a loss; report what actually happened.
    return { before: player.rating, after, change: after - player.rating };
  });
};

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

export interface RatingTier {
  readonly id: string;
  readonly name: string;
  /** Lowest rating in the band. The first is `MIN_RATING`. */
  readonly min: number;
  /** `#rrggbb`, for the chip. */
  readonly color: string;
}

/**
 * The ladder, lowest first.
 *
 * Bands widen going up, which is the shape every ladder in the genre has and
 * is not arbitrary: rating is a logistic scale, so a fixed 150 points is a
 * much larger skill gap at the top than at the bottom. Equal bands would put
 * most of the population in two tiers and leave the top three empty.
 */
export const RATING_TIERS: readonly RatingTier[] = Object.freeze([
  { id: 'bronze', name: 'Bronze', min: MIN_RATING, color: '#b0783c' },
  { id: 'silver', name: 'Silver', min: 500, color: '#adbccd' },
  { id: 'gold', name: 'Gold', min: 700, color: '#e0b544' },
  { id: 'platinum', name: 'Platinum', min: 900, color: '#7fd0d8' },
  { id: 'diamond', name: 'Diamond', min: 1150, color: '#6f8cf0' },
  { id: 'champion', name: 'Champion', min: 1450, color: '#b06fe8' },
  { id: 'grand-champion', name: 'Grand Champion', min: 1800, color: '#ef5a6a' },
]);

export const tierFor = (rating: number): RatingTier => {
  let found = RATING_TIERS[0];
  for (const tier of RATING_TIERS) {
    if (rating >= tier.min) found = tier;
  }
  return found;
};

/** The next tier up, or null at the top of the ladder. */
export const nextTier = (rating: number): RatingTier | null => {
  for (const tier of RATING_TIERS) {
    if (rating < tier.min) return tier;
  }
  return null;
};

/**
 * Progress through the current band, 0..1. Returns 1 in the top tier — there
 * is nothing above it, and a bar stuck at 0 forever is worse than a full one.
 */
export const tierProgress = (rating: number): number => {
  const current = tierFor(rating);
  const next = nextTier(rating);
  if (next === null) return 1;
  const span = next.min - current.min;
  if (span <= 0) return 1;
  return Math.max(0, Math.min(1, (rating - current.min) / span));
};

/**
 * What a rating is called: the tier, and "Provisional" until it means
 * anything. A tier shown after two games is a label the system does not yet
 * believe, printed with the same confidence as one it does.
 */
export const ratingLabel = (rating: number, matchesPlayed: number): string =>
  matchesPlayed < PROVISIONAL_MATCHES
    ? `Provisional · ${matchesPlayed} / ${PROVISIONAL_MATCHES}`
    : tierFor(rating).name;

// ---------------------------------------------------------------------------
// The record a client is shown
// ---------------------------------------------------------------------------

/** One mode's standing. The server's `user_ratings` row, as JSON. */
export interface RatingRecord {
  readonly mode: string;
  readonly rating: number;
  readonly played: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

export const emptyRating = (mode: string): RatingRecord => ({
  mode,
  rating: STARTING_RATING,
  played: 0,
  wins: 0,
  losses: 0,
  draws: 0,
});

/** Win rate as a percentage, 0 when nothing has been played. */
export const winRate = (record: RatingRecord): number =>
  record.played === 0 ? 0 : Math.round((record.wins / record.played) * 100);
